import { createI18nSettings } from '@kit/i18n';

/**
 * The default language of the application.
 * This is used as a fallback language when the selected language is not supported.
 *
 */
const defaultLanguage = process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? 'pl';

/**
 * The English demo language. Served only under the `/en` path
 * (see `i18n.server.ts` and `lib/i18n/request-language.ts`).
 */
export const ENGLISH_LANGUAGE = 'en';

/**
 * The list of supported languages.
 * The first entry is the default (and the fallback for missing keys).
 */
export const languages: string[] = [defaultLanguage, ENGLISH_LANGUAGE];

/**
 * The name of the cookie that stores the selected language.
 *
 * NOTE: the cookie is no longer consulted when resolving the language —
 * the language is derived from the URL path only. The constant is kept for
 * callers that still reference it.
 */
export const I18N_COOKIE_NAME = 'lang';

/**
 * The default array of Internationalization (i18n) namespaces.
 * These namespaces are commonly used in the application for translation purposes.
 *
 * Add your own namespaces here
 **/
export const defaultI18nNamespaces = [
  'common',
  'auth',
  'account',
  'teams',
  'billing',
  'marketing',
  'feedback',
  'interactions',
];

/**
 * Get the i18n settings for the given language and namespaces.
 * If the language is not supported, it will fall back to the default language.
 * @param language
 * @param ns
 */
export function getI18nSettings(
  language: string | undefined,
  ns: string | string[] = defaultI18nNamespaces,
) {
  let lng = language ?? defaultLanguage;

  if (!languages.includes(lng)) {
    console.warn(
      `Language "${lng}" is not supported. Falling back to "${defaultLanguage}"`,
    );

    lng = defaultLanguage;
  }

  return createI18nSettings({
    language: lng,
    namespaces: ns,
    languages,
  });
}
